export type DeferredAnalysisJobStatus =
  | "initializing"
  | "queued"
  | "running"
  | "completed"
  | "completed_with_failures"
  | "failed";

export type DeferredAnalysisJobScope = "task" | "entryConfig";

export interface DeferredAnalysisJobIdParts {
  trialId: string;
  experimentId: string;
  taskId: string;
  entryConfigId?: string | null;
  scope?: DeferredAnalysisJobScope;
}

export function safeDeferredAnalysisIdPart(value: string): string {
  return value.replace(/[^A-Za-z0-9_-]/g, "_").slice(0, 220);
}

export function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

export function finiteNumber(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export type QuarterTurnDegrees = 0 | 90 | 180 | 270;

/** Normalize a degrees value to a quarter turn (0/90/180/270). Throws if it is
 *  not a multiple of 90. Missing/invalid input is treated as 0. */
export function normalizeQuarterTurnDegrees(value: unknown): QuarterTurnDegrees {
  const degrees = finiteNumber(value) ?? 0;
  const rounded = Math.round(degrees / 90) * 90;
  if (Math.abs(degrees - rounded) > 1e-6) {
    throw new Error(`image.rotationToDepthFrameDegrees must be a multiple of 90, got ${degrees}`);
  }
  return (((rounded % 360) + 360) % 360) as QuarterTurnDegrees;
}

export function inverseQuarterTurnDegrees(rotation: QuarterTurnDegrees): QuarterTurnDegrees {
  return normalizeQuarterTurnDegrees(360 - rotation);
}

/** Rotate a normalized ([0,1]²) point by a quarter turn. Used to map between the
 *  saved display frame and the depth-aligned frame; shared so the client's live
 *  measurement recompute uses the exact same convention as the server. */
export function rotateNormalizedPoint(
  point: { x: number; y: number },
  rotationDegrees: QuarterTurnDegrees
): { x: number; y: number } {
  switch (rotationDegrees) {
    case 90:
      return { x: 1 - point.y, y: point.x };
    case 180:
      return { x: 1 - point.x, y: 1 - point.y };
    case 270:
      return { x: point.y, y: 1 - point.x };
    case 0:
    default:
      return point;
  }
}

export function buildDeferredAnalysisJobId({
  trialId,
  experimentId,
  taskId,
  entryConfigId,
  scope = "entryConfig",
}: DeferredAnalysisJobIdParts): string {
  if (scope === "task") {
    return [
      "manual",
      safeDeferredAnalysisIdPart(trialId),
      safeDeferredAnalysisIdPart(experimentId),
      safeDeferredAnalysisIdPart(taskId),
      "all",
    ].join("_");
  }

  if (!entryConfigId) {
    throw new Error("entryConfigId is required for entryConfig-scoped deferred analysis jobs");
  }

  return [
    "manual",
    safeDeferredAnalysisIdPart(trialId),
    safeDeferredAnalysisIdPart(experimentId),
    safeDeferredAnalysisIdPart(taskId),
    safeDeferredAnalysisIdPart(entryConfigId),
  ].join("_");
}

export function timestampToMillis(value: unknown): number | undefined {
  if (!value) return undefined;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (value instanceof Date) return value.getTime();
  if (typeof value === "string") {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? undefined : parsed;
  }
  if (typeof value === "object") {
    const timestamp = value as {
      toMillis?: () => number;
      seconds?: number;
      nanoseconds?: number;
      _seconds?: number;
      _nanoseconds?: number;
    };
    if (typeof timestamp.toMillis === "function") return timestamp.toMillis();
    if (typeof timestamp.seconds === "number") {
      const nanoseconds = typeof timestamp.nanoseconds === "number" ? timestamp.nanoseconds : 0;
      return timestamp.seconds * 1000 + Math.floor(nanoseconds / 1_000_000);
    }
    if (typeof timestamp._seconds === "number") {
      const nanoseconds = typeof timestamp._nanoseconds === "number" ? timestamp._nanoseconds : 0;
      return timestamp._seconds * 1000 + Math.floor(nanoseconds / 1_000_000);
    }
  }
  return undefined;
}
